import { useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../api";
import { Card } from "../../ui";
import { DateInput } from "../../DateInput";
import { Label, TAGS } from "./shared";

const EMPTY_FORM = {
  title: "",
  description: "",
  tag: "Boleto",
  assigned_to: "specific",
  selected: [],
  send_email: true,
  valor: "",
  due_date: "",
  boleto_type: "Mensalidade",
};

const validate = (form, file, isBoleto) => {
  if (!file) return "Selecione um arquivo.";
  if (isBoleto && form.assigned_to === "all")
    return "Boletos devem ser enviados a associados específicos, não a todos.";
  if (form.assigned_to === "specific" && form.selected.length === 0)
    return "Selecione ao menos um destinatário.";
  return null;
};

const buildFormData = (form, file) => {
  const fd = new FormData();
  fd.append("title", form.title);
  fd.append("description", form.description);
  fd.append("tag", form.tag);
  fd.append("assigned_to", form.assigned_to === "all" ? "all" : form.selected.join(","));
  fd.append("file", file);
  fd.append("send_email", form.send_email ? "true" : "false");
  if (form.tag === "Boleto") {
    if (form.valor) fd.append("valor", form.valor.toString().replace(",", "."));
    if (form.due_date) fd.append("due_date", form.due_date);
    if (form.boleto_type) fd.append("boleto_type", form.boleto_type);
  }
  return fd;
};

const notifyEmailStatus = (sendEmail, es) => {
  if (!sendEmail || !es) return toast.success("Arquivo enviado!");
  if (es.sent > 0)
    return toast.success(
      `Arquivo enviado! E-mail para ${es.sent} destinatário(s)` +
        (es.failed > 0 ? ` · ${es.failed} falha(s)` : "")
    );
  if (es.skipped_no_email > 0)
    return toast.warning("Arquivo salvo, mas os destinatários selecionados não possuem e-mail cadastrado.");
  if (es.failed > 0) return toast.warning("Arquivo salvo, mas o envio de e-mail falhou. Verifique SMTP.");
  return toast.success("Arquivo enviado!");
};

export function UploadForm({ associados, onDone, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const isBoleto = form.tag === "Boleto";

  const toggleSel = (id) =>
    setForm((f) => ({
      ...f,
      selected: f.selected.includes(id) ? f.selected.filter((x) => x !== id) : [...f.selected, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(form, file, isBoleto);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const res = await api.post("/admin/files", buildFormData(form, file), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notifyEmailStatus(form.send_email, res.data?.email_status);
      setForm(EMPTY_FORM);
      setFile(null);
      onDone();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 mb-5">
          <form data-testid="upload-form" onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Título *</Label>
              <input
                data-testid="upload-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div>
              <Label>Tipo *</Label>
              <select
                data-testid="upload-tag"
                value={form.tag}
                onChange={(e) => {
                  const newTag = e.target.value;
                  setForm((f) => ({
                    ...f,
                    tag: newTag,
                    // Boletos nunca podem ir para "todos"
                    assigned_to: newTag === "Boleto" ? "specific" : f.assigned_to,
                  }));
                }}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
              >
                {TAGS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <textarea
                data-testid="upload-description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[80px]"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Arquivo (PDF, imagem) *</Label>
              <input
                data-testid="upload-file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Destinatários *</Label>
              <div className="mt-2 flex gap-3 flex-wrap">
                <label className={`inline-flex items-center gap-2 text-sm ${isBoleto ? "opacity-40 cursor-not-allowed" : ""}`}>
                  <input
                    data-testid="upload-target-all"
                    type="radio"
                    checked={form.assigned_to === "all"}
                    disabled={isBoleto}
                    onChange={() => setForm({ ...form, assigned_to: "all" })}
                  />
                  Todos os associados ativos
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    data-testid="upload-target-specific"
                    type="radio"
                    checked={form.assigned_to === "specific"}
                    onChange={() =>
                      setForm({ ...form, assigned_to: "specific" })
                    }
                  />
                  Selecionar associados
                </label>
              </div>
              {isBoleto && (
                <p
                  data-testid="boleto-restriction-note"
                  className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm"
                >
                  Boletos são sempre nominais — selecione os associados destinatários.
                  O arquivo será enviado como <strong>anexo</strong> ao e-mail financeiro cadastrado.
                </p>
              )}
              {form.assigned_to === "specific" && (
                <div className="mt-3 max-h-60 overflow-y-auto border border-slate-200 rounded-sm">
                  {associados.length === 0 && (
                    <div className="p-3 text-sm text-slate-500 inline-flex items-center gap-2">
                      <Users size={14} /> Nenhum associado ativo no momento.
                    </div>
                  )}
                  {associados.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 p-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        data-testid={`select-${u.id}`}
                        type="checkbox"
                        checked={form.selected.includes(u.id)}
                        onChange={() => toggleSel(u.id)}
                      />
                      <div className="text-sm">
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {isBoleto && (
              <div className="md:col-span-2 grid md:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
                <div>
                  <Label>Valor (R$) *</Label>
                  <input
                    data-testid="upload-valor"
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 250,00"
                    value={form.valor}
                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
                    required
                  />
                </div>
                <div>
                  <Label>Vencimento</Label>
                  <DateInput
                    testid="upload-due-date"
                    value={form.due_date}
                    onChange={(v) => setForm({ ...form, due_date: v })}
                  />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <select
                    data-testid="upload-boleto-type"
                    value={form.boleto_type}
                    onChange={(e) => setForm({ ...form, boleto_type: e.target.value })}
                    className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
                  >
                    <option>Mensalidade</option>
                    <option>Anuidade</option>
                    <option>Taxa Extra</option>
                    <option>Multa</option>
                    <option>Outro</option>
                  </select>
                </div>
                <p className="md:col-span-3 text-xs text-slate-500">
                  O número do boleto é extraído do nome do arquivo. Padrão:
                  <strong> Boleto &lt;NUMERO&gt; - &lt;ASSOCIADO&gt;.pdf</strong>.
                </p>
              </div>
            )}
            <div className="md:col-span-2 flex items-center gap-2 border-t border-slate-100 pt-3">
              <input
                data-testid="upload-send-email"
                id="upload-send-email"
                type="checkbox"
                checked={form.send_email}
                onChange={(e) =>
                  setForm({ ...form, send_email: e.target.checked })
                }
                className="h-4 w-4 accent-[#002776]"
              />
              <label
                htmlFor="upload-send-email"
                className="text-sm text-slate-700 cursor-pointer select-none"
              >
                Enviar e-mail de notificação aos destinatários
                {isBoleto && (
                  <span className="text-slate-500">
                    {" "}
                    (o arquivo será anexado ao e-mail financeiro)
                  </span>
                )}
              </label>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                data-testid="upload-submit"
                disabled={busy}
                className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                {busy ? "Enviando..." : "Enviar"}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
              >
                Cancelar
              </button>
            </div>
          </form>
    </Card>
  );
}
